import {
    Component,
    computed,
    effect,
    inject,
    input,
    model,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Fluid } from 'primeng/fluid';
import { InputText } from 'primeng/inputtext';
import { Tag } from 'primeng/tag';
import { CategoryType, RealmBrandingConfig, RealmConfig } from '@/lib-backend';
import { BrandKit, BrandKitService } from '@/app/services/brand-kit.service';
import { AiAssistService } from '@/app/services/ai-assist.service';
import { AuthService } from '@/app/services/auth.service';
import { SharedContentService } from '@/app/services/shared-content.service';
import { AiSeoAssist } from '@/app/components/ai-seo-assist';
import { AiLogoGenerate, LogoPick } from '@/app/components/ai-logo-generate';
import { AiOgGenerate } from '@/app/components/ai-og-generate';
import { AssetUpload } from '@/app/components/asset-upload';
import { sharedContentPreviewUrl } from '@/app/utils/shared-content-url';
import { BrandingPreview } from './branding-preview';

/** The two halves of the split wordmark logo (`Primary` / `Secondary`). */
interface LogoSplit {
    primary: string;
    secondary: string;
}

@Component({
    selector: 'app-website-info-branding',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        Fluid,
        InputText,
        Tag,
        BrandingPreview,
        AiSeoAssist,
        AiLogoGenerate,
        AiOgGenerate,
        AssetUpload,
    ],
    templateUrl: './branding-tab.html',
})
export class BrandingTab {
    private readonly brandKitService = inject(BrandKitService);
    private readonly aiAssist = inject(AiAssistService);
    private readonly auth = inject(AuthService);
    private readonly sharedContent = inject(SharedContentService);

    readonly realm = input.required<RealmConfig>();
    readonly draft = model.required<RealmBrandingConfig>();

    protected readonly currentYear = new Date().getFullYear();

    /**
     * Brand-kit resolved from the realm's assigned Site Template (style
     * descriptor, OG base-plate, brand-icon references), read via the Admin
     * API's getSiteTemplateFile. Null until resolved or when no template /
     * brand-kit. Feeds the AI dialogs so the operator doesn't paste these.
     */
    protected readonly brandKit = signal<BrandKit | null>(null);

    /** The style descriptor forwarded to the AI suggestions (from the brand-kit). */
    protected readonly effectiveStyle = computed<Record<
        string,
        unknown
    > | null>(() => this.brandKit()?.style ?? null);

    // ── AI affordance gating ─────────────────────────────────────────────────
    // Each AI button is disabled (not hidden) with a hint when the service is
    // down or the template lacks the element that action needs.

    /** Non-null when the AI Assist service itself is unreachable. */
    private readonly serviceDownReason = computed<string | null>(() =>
        this.aiAssist.status() === 'down'
            ? 'AI Assist service is unavailable — check the status indicator in the top bar.'
            : null,
    );

    /** Logo generation needs brand reference images from the template. */
    protected readonly logoAiReason = computed<string | null>(() => {
        const down = this.serviceDownReason();
        if (down) return down;
        return (this.brandKit()?.references.length ?? 0) === 0
            ? 'The assigned Site Template has no brand reference images for logo generation.'
            : null;
    });
    protected readonly logoAiDisabled = computed(
        () => this.logoAiReason() !== null,
    );

    /** OG generation needs the template's text-free base plate. */
    protected readonly ogAiReason = computed<string | null>(() => {
        const down = this.serviceDownReason();
        if (down) return down;
        return !this.brandKit()?.ogBasePlate
            ? 'The assigned Site Template has no OG base-plate image.'
            : null;
    });
    protected readonly ogAiDisabled = computed(
        () => this.ogAiReason() !== null,
    );

    /** Text helpers (SEO / refine) need no template element — only the service. */
    protected readonly textAiReason = computed<string | null>(() =>
        this.serviceDownReason(),
    );
    protected readonly textAiDisabled = computed(
        () => this.textAiReason() !== null,
    );

    /** Shared-content uploads are restricted to global admins on the backend. */
    protected readonly uploadReason = computed<string | null>(() =>
        this.auth.isGlobalAdmin()
            ? null
            : 'Only a global admin can upload files to the shared content store.',
    );
    protected readonly uploadDisabled = computed(
        () => this.uploadReason() !== null,
    );

    /** True while an AI-picked image is being copied into the shared content store. */
    protected readonly rehosting = signal(false);

    constructor() {
        // Resolve the brand-kit from the realm's assigned SiteTemplate (read via
        // the Admin API's getSiteTemplateFile) whenever that assignment changes.
        effect(() => {
            const templateId = this.realm().SiteTemplateId;
            this.brandKit.set(null);
            void this.brandKitService.resolve(templateId).then((kit) => {
                if (this.realm().SiteTemplateId === templateId)
                    this.brandKit.set(kit);
            });
        });

        // Probe the AI service once so the gating reflects real availability
        // (the topbar indicator also probes; this covers a cold tab).
        if (this.aiAssist.status() === 'unknown') {
            void this.aiAssist.refreshHealth();
        }
    }

    /** Category-derived tone hint forwarded to the AI suggestions. */
    protected readonly vertical = computed<string | null>(() => {
        switch (this.realm().CategorySpecific.type) {
            case CategoryType.SkillBasedGames:
                return 'skill_games';
            case CategoryType.LuckyBasedGames:
                return 'social_casino';
            default:
                return null;
        }
    });

    protected readonly dirty = computed(
        () =>
            JSON.stringify(this.draft()) !==
            JSON.stringify(this.realm().Branding),
    );

    /** True when any of the given fields differs from the saved branding. */
    protected fieldDirty(...keys: (keyof RealmBrandingConfig)[]): boolean {
        const draft = this.draft();
        const saved = this.realm().Branding;
        return keys.some(
            (key) => (draft[key] ?? null) !== (saved[key] ?? null),
        );
    }

    /** Revert the given fields to their saved values (per-field reset). */
    protected resetFields<K extends keyof RealmBrandingConfig>(
        ...keys: K[]
    ): void {
        const saved = this.realm().Branding;
        const patch: Partial<Pick<RealmBrandingConfig, K>> = {};
        for (const key of keys) {
            patch[key] = saved[key];
        }
        this.draft.set({ ...this.draft(), ...patch });
    }

    protected setField<K extends keyof RealmBrandingConfig>(
        key: K,
        value: RealmBrandingConfig[K],
    ): void {
        this.draft.set({ ...this.draft(), [key]: value });
    }

    /**
     * The brand name is the single source of truth: `DisplayName` is what the
     * operator types, and `Name` (in-copy) + `FullName` (legal) are mirrored
     * from it — in practice identical across brands. Hidden fields stay in sync.
     */
    protected setBrandName(value: string): void {
        const v = value ?? '';
        this.draft.set({
            ...this.draft(),
            DisplayName: v,
            Name: v,
            FullName: v,
        });
    }

    /** Apply an AI-suggested (or custom) logo split to the two logo halves. */
    protected applyLogoSplit(split: LogoSplit): void {
        this.draft.set({
            ...this.draft(),
            Primary: split.primary,
            Secondary: split.secondary,
        });
    }

    /**
     * Admin-proposed split of the brand wordmark — a deterministic guess (no
     * AI): split at a camelCase boundary, else at a vowel→consonant boundary
     * near the middle (e.g. "Livecazino" → "Live" / "cazino"). The operator can
     * then tweak the halves manually or ask AI for alternatives.
     */
    protected autoSplitLogo(): void {
        const split = this.proposeSplit(this.draft().DisplayName);
        this.applyLogoSplit(split);
    }

    private proposeSplit(name: string): LogoSplit {
        const w = (name ?? '').trim();
        if (w.length < 2) return { primary: w, secondary: '' };

        // camelCase boundary: "LiveCazino" → before the inner capital.
        const camel = w.slice(1).search(/\p{Lu}/u);
        if (camel >= 0) {
            const i = camel + 1;
            return { primary: w.slice(0, i), secondary: w.slice(i) };
        }

        // Otherwise a vowel→consonant boundary nearest the middle.
        const vowels = 'aeiouyAEIOUY';
        const mid = Math.round(w.length / 2);
        for (let d = 0; d <= 2; d++) {
            for (const i of [mid - d, mid + d]) {
                if (
                    i > 0 &&
                    i < w.length &&
                    vowels.includes(w[i - 1]) &&
                    !vowels.includes(w[i])
                ) {
                    return { primary: w.slice(0, i), secondary: w.slice(i) };
                }
            }
        }
        return { primary: w.slice(0, mid), secondary: w.slice(mid) };
    }

    /**
     * Apply an AI-generated brand mark: the logo icon and its derived favicon
     * (A2) are set together — there is no separate favicon field. The picked
     * candidates live in the AI service's working bucket, so they are first
     * re-hosted into the shared content store; on a failed copy the draft is
     * left untouched (the config must never reference the AI bucket).
     */
    protected async applyLogo(pick: LogoPick): Promise<void> {
        const realmId = this.realm().RealmId;
        this.rehosting.set(true);
        try {
            const logoUrl = await this.sharedContent.rehostBrandingUrl(
                realmId,
                'logo-icon',
                pick.logoUrl,
            );
            if (!logoUrl) return;

            let faviconUrl = logoUrl;
            if (pick.faviconUrl && pick.faviconUrl !== pick.logoUrl) {
                // On a failed favicon copy fall back to the logo mark — same
                // degradation applyLogo always had for a missing favicon.
                faviconUrl =
                    (await this.sharedContent.rehostBrandingUrl(
                        realmId,
                        'favicon',
                        pick.faviconUrl,
                    )) ?? logoUrl;
            }

            this.draft.set({
                ...this.draft(),
                LogoIconUrl: logoUrl,
                FaviconUrl: faviconUrl,
            });
        } finally {
            this.rehosting.set(false);
        }
    }

    /** Apply an AI-generated OG image — re-hosted like the logo (see applyLogo). */
    protected async applyOg(url: string): Promise<void> {
        this.rehosting.set(true);
        try {
            const ogUrl = await this.sharedContent.rehostBrandingUrl(
                this.realm().RealmId,
                'og',
                url,
            );
            if (ogUrl) this.setField('OgImageUrl', ogUrl);
        } finally {
            this.rehosting.set(false);
        }
    }

    /** Operator-uploaded brand mark: mirror the favicon like the AI flow does. */
    protected onLogoUploaded(url: string): void {
        this.draft.set({
            ...this.draft(),
            LogoIconUrl: url,
            FaviconUrl: url,
        });
    }

    /** The logo icon and favicon are one mark — they are also cleared together. */
    protected clearLogo(): void {
        this.draft.set({
            ...this.draft(),
            LogoIconUrl: null,
            FaviconUrl: null,
        });
    }

    /**
     * Resolve a stored shared-content URL for preview: bucket-first, with the
     * realm domain as fallback when the bucket is not configured. Absolute URLs
     * pass through unchanged.
     */
    protected assetPreviewUrl(url: string | null | undefined): string | null {
        if (!url?.trim()) return null;
        return sharedContentPreviewUrl(url);
    }

    protected reset(): void {
        this.draft.set({ ...this.realm().Branding });
    }
}
